import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { downloadBlob } from "./api";

// Contexto de la página para el copiloto: ruta, entidad, filtros, selección y vista visible.
const store = { extra: {} };

export const setCopilotoCtx = (patch) => { store.extra = { ...store.extra, ...patch }; };
export const clearCopilotoCtx = () => { store.extra = {}; };

const ENT = [[/^\/deals\/([0-9a-f]{24})/, "deal"], [/^\/captacion\/pipeline\/([0-9a-f]{24})/, "prospecto_captacion"]];

export const copilotoCtx = (loc) => {
  const sp = Object.fromEntries(new URLSearchParams(loc.search));
  let entidad = null;
  for (const [rx, tipo] of ENT) { const m = loc.pathname.match(rx); if (m) entidad = { tipo, id: m[1] }; }
  if (!entidad && sp.loan) entidad = { tipo: "credito", id: sp.loan };
  if (!entidad && sp.id) entidad = { tipo: loc.pathname.split("/")[1], id: sp.id };
  const { tab, loan, id, ...filtros } = sp;
  return { ruta: loc.pathname, entidad, filtros, pestana: tab || null, vista: document.title, ...store.extra };
};

const qs = (ruta, params) => {
  const [p, q] = ruta.split("?");
  const s = new URLSearchParams(q || "");
  Object.entries(params || {}).forEach(([k, v]) => v !== null && v !== undefined && v !== "" && s.set(k, Array.isArray(v) ? v.join(",") : typeof v === "object" ? JSON.stringify(v) : String(v)));
  const t = s.toString();
  return t ? `${p}?${t}` : p;
};

// Ejecuta en el navegador una acción de UI validada por el servidor (solo lectura, sin confirmación).
export const ejecutarUi = (a, navigate, loc) => {
  const emit = () => window.dispatchEvent(new CustomEvent("copiloto:ui", { detail: a }));
  if (a.tipo === "navegar" || a.tipo === "filtros") { navigate(qs(a.ruta, a.params)); setTimeout(emit, 400); return; }
  if (a.tipo === "limpiar") { navigate(loc.pathname); clearCopilotoCtx(); setTimeout(emit, 200); return; }
  if (a.tipo === "pestana") { navigate(qs(loc.pathname + loc.search, { tab: a.pestana })); setTimeout(emit, 200); return; }
  if (a.tipo === "ordenar") { navigate(qs(loc.pathname + loc.search, { orden: `${a.campo}:${a.direccion || "asc"}` })); setTimeout(emit, 200); return; }
  if (a.tipo === "agrupar") { navigate(qs(loc.pathname + loc.search, { agrupar: a.campo })); setTimeout(emit, 200); return; }
  if (a.tipo === "seleccionar") { setCopilotoCtx({ seleccion: a.ids || [] }); emit(); return; }
  if (a.tipo === "descargar") { downloadBlob(a.url, a.archivo, a.metodo, a.cuerpo || undefined); return; }
  if (a.tipo === "vista_guardada") toast.success(a.etiqueta);
  // «limite»: el chip «Ir a la pantalla» lo decide la persona
};

// Las pantallas se suscriben para aplicar filtros/orden/selección en su propio estado.
export const useCopilotoUi = (handler) => {
  useEffect(() => {
    const on = (e) => handler(e.detail);
    window.addEventListener("copiloto:ui", on);
    return () => window.removeEventListener("copiloto:ui", on);
  }, [handler]);
};

export const CHIP = { limite: "Requiere a una persona",  navegar: "Navegó", filtros: "Filtró", limpiar: "Quitó filtros", pestana: "Cambió de pestaña", ordenar: "Ordenó", agrupar: "Agrupó", seleccionar: "Seleccionó", descargar: "Descargó", vista_guardada: "Guardó la vista" };

export const preguntarCopiloto = (texto) => window.dispatchEvent(new CustomEvent("copiloto:preguntar", { detail: texto }));

// Una línea por pantalla: aplica en vivo filtros/pestaña del copiloto y publica el contexto visible.
export const useCopilotoFiltros = (pantallas, apply, ctx) => {
  const ps = [].concat(pantallas);
  const ref = useRef(apply);
  ref.current = apply;
  const key = JSON.stringify(ctx || {});
  useEffect(() => { if (ctx) setCopilotoCtx(ctx); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = (e) => {
      const a = e.detail;
      if (a.tipo === "limpiar") ref.current(null);
      else if (a.tipo === "filtros" && ps.includes(a.pantalla)) ref.current(a.params || {});
      else if (a.tipo === "pestana") ref.current({ tab: a.pestana });
    };
    window.addEventListener("copiloto:ui", on);
    return () => window.removeEventListener("copiloto:ui", on);
  }, [ps.join()]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const mezclar = (setF, vacio) => (p) => setF(p === null ? vacio : (x) => ({ ...x, ...Object.fromEntries(Object.entries(p).filter(([k]) => k in vacio || k in x)) }));

// ?resaltar=<selector>: resalta el botón que debe presionar una persona (límites del copiloto)
export const useResaltar = (loc) => {
  useEffect(() => {
    const sel = new URLSearchParams(loc.search).get("resaltar");
    if (!sel) return undefined;
    let n = 0;
    const t = setInterval(() => {
      let el = null;
      try { el = document.querySelector(sel); } catch { clearInterval(t); return; }
      if (el || ++n > 20) clearInterval(t);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("copiloto-resaltado"); el.setAttribute("data-copiloto-resaltado", "1"); setTimeout(() => el.classList.remove("copiloto-resaltado"), 8000); }
    }, 300);
    return () => clearInterval(t);
  }, [loc.search]);
};
